/* Settings → Updates (super admins). Managed installs (flux-panel-install) get a manual Update
 * button: never automatic, refused while a match is live. The panel restarts during an update, so
 * progress is polled from /api/update/status and requests failing for a few seconds is expected.
 * Revert: pick an older release (the updater lists 3.0.2 and newer) - same steps, downgrade: true. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '../AlertModal';
import { SvIcon } from '../servers/svIcons';
import { CopyButton } from '../ext/extUi';
import { formatLocalDateTime } from '../../utils/timeUtils';
import './updates.css';

// same order as server/updater.js: x.y.z-beta.N sorts before x.y.z
const semver = (v) => {
  const m = String(v || '').match(/^v?(\d+)\.(\d+)\.(\d+)(?:-beta\.(\d+))?$/);
  return m ? [+m[1], +m[2], +m[3], m[4] === undefined ? Infinity : +m[4]] : [0, 0, 0, 0];
};
const cmpVer = (a, b) => { const x = semver(a), y = semver(b); for (let i = 0; i < 4; i++) if (x[i] !== y[i]) return x[i] < y[i] ? -1 : 1; return 0; };
const isBeta = (v) => /-beta\.\d+$/.test(String(v || ''));

export default function UpdatesPanel() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [checking, setChecking] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [revertTo, setRevertTo] = useState('');
  const [confirmRevert, setConfirmRevert] = useState(false);
  const [starting, setStarting] = useState(false);
  const [actionError, setActionError] = useState('');
  const [restarting, setRestarting] = useState(false); // panel unreachable during the update
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get('/api/update/status', { timeout: 8000 });
      setData(r.data); setLoadError(''); setRestarting(false);
      return r.data;
    } catch (e) {
      if (pollRef.current) setRestarting(true); else setLoadError(e.response?.data?.error || t('updates.loadFailed', 'Could not load the update status.'));
      return null;
    }
  }, [t]);

  // Opening the Updates tab looks for new versions right away (same as "Check now"), quietly: the
  // status shows at once, the list refreshes when the check answers; nothing runs during an update.
  useEffect(() => {
    let alive = true;
    (async () => {
      const d = await load();
      const up = d && d.updater;
      if (!alive || !d || !d.managed || !up || up.updating || up.status === 'updating') return;
      setChecking(true);
      try { await axios.post('/api/update/check'); if (alive) await load(); }
      catch (_) { /* the automatic check stays quiet; "Check now" shows errors */ }
      finally { if (alive) setChecking(false); }
    })();
    return () => { alive = false; };
  }, [load]);

  const u = data?.updater;
  const busy = !!(u && (u.updating || u.status === 'updating'));

  // poll every 2 s while an update runs (also across the panel restart)
  useEffect(() => {
    if (!busy && !starting && !restarting) { clearInterval(pollRef.current); pollRef.current = null; return undefined; }
    if (!pollRef.current) pollRef.current = setInterval(load, 2000);
    return undefined;
  }, [busy, starting, restarting, load]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const check = async () => {
    setChecking(true); setActionError('');
    try { await axios.post('/api/update/check'); await load(); }
    catch (e) { setActionError(e.response?.data?.error || t('updates.checkFailed', 'Check failed.')); }
    finally { setChecking(false); }
  };

  const start = async (version, downgrade = false) => {
    setConfirm(false); setConfirmRevert(false); setStarting(true); setActionError('');
    try {
      await axios.post('/api/update/start', { version, downgrade });
      pollRef.current = pollRef.current || setInterval(load, 2000);
      await load();
    } catch (e) {
      const err = e.response?.data;
      setActionError(err?.error === 'match_live'
        ? t('updates.liveRefused', 'A match is live - update when no match is running.')
        : err?.error === 'too_old'
          ? t('updates.revertTooOld', 'Versions older than v{{v}} cannot be installed from the panel.', { v: err.min })
          : (err?.error || t('updates.startFailed', 'The update could not be started.')));
    } finally { setStarting(false); }
  };

  if (loadError) return <div className="up-error">{loadError}</div>;
  if (!data) return <div className="up-muted">{t('common.loading', 'Loading...')}</div>;

  const cur = data.current || {};
  const live = data.live || [];
  const recent = !!(u && u.finishedAt && Date.now() - new Date(u.finishedAt).getTime() < 24 * 3600 * 1000);
  // the server already runs the new release, but this page is still the old bundle
  const pageOutdated = !!(u && u.status === 'done' && cur.commit && process.env.REACT_APP_BUILD_COMMIT
    && process.env.REACT_APP_BUILD_COMMIT !== cur.commit);
  const reverting = u?.kind === 'revert';
  const running = u?.current || cur.version;
  const older = (u?.versions || []).filter(v => cmpVer(v, running) < 0);
  const pick = older.includes(revertTo) ? revertTo : (older[0] || '');
  // the last result only while it is about the version running now (a version installed by hand,
  // outside the updater, leaves an older result in the updater's state)
  const showDone = recent && u?.status === 'done' && !!u.target && u.target === running;
  const showFail = recent && (u?.status === 'failed' || u?.status === 'rolled_back') && !!u.error && (!u.previous || u.previous === running);

  return (
    <div className="up-wrap">
      {/* current */}
      <div className="up-current">
        <span className="up-ic"><SvIcon name="server" size={16} /></span>
        <div>
          <div className="up-label">{t('updates.running', 'This panel is running')}</div>
          <div className="up-ver">
            v{cur.version}
            {cur.commit && <span className="up-commit">{cur.commit}</span>}
            {u?.channel === 'beta' && (
              <span className="up-beta" title={t('updates.betaHint', 'This panel also receives beta (test) versions built from the development branch.')}>
                {t('updates.betaChannel', 'Beta channel')}
              </span>
            )}
          </div>
        </div>
        {data.managed && (
          <button type="button" className="st-btn" onClick={check} disabled={checking || busy}>
            <SvIcon name="refresh" size={13} />{checking ? t('updates.checking', 'Checking…') : t('updates.checkNow', 'Check now')}
          </button>
        )}
      </div>

      {!data.managed && (
        <div className="up-box">
          <b>{t('updates.notManagedTitle', 'Updates are managed by hand on this install')}</b>
          <p>{t('updates.notManagedText', 'This panel was built from the source code. Update it with git pull and a rebuild, or switch it to the managed setup (release image + Update button) with the Flux setup script.')}</p>
        </div>
      )}

      {data.managed && !u && (
        <div className="up-error">{t('updates.updaterDown', 'The updater service is not answering')}{data.updaterError ? `: ${data.updaterError}` : ''}</div>
      )}

      {data.managed && u && (<>
        <div className="up-meta">
          <span>{t('updates.latest', 'Latest release')}: <b>{u.latest ? `v${u.latest}` : '–'}</b></span>
          <span>{t('updates.lastCheck', 'Last checked')}: <b>{u.lastCheck ? formatLocalDateTime(u.lastCheck) : '–'}</b></span>
        </div>
        {u.checkError && <div className="up-error">{u.checkError}</div>}

        {/* progress / result */}
        {(busy || starting || restarting) && (
          <div className="up-progress">
            <span className="st-spin" />
            <div>
              <b>{reverting
                ? t('updates.revertingTo', 'Reverting to v{{v}}…', { v: u.target })
                : t('updates.updatingTo', 'Updating to v{{v}}…', { v: u.target || u.latest })}</b>
              <p>{restarting ? t('updates.restarting', 'The panel is restarting - this page reconnects by itself.') : t('updates.keepOpen', 'Keep this page open. CS2 logs keep arriving during the restart.')}</p>
            </div>
          </div>
        )}
        {!busy && !restarting && showDone && (
          <div className="up-ok">
            <b>{reverting ? t('updates.revertedTitle', 'Reverted to v{{v}}', { v: u.target }) : t('updates.doneTitle', 'Updated to v{{v}}', { v: u.target })}</b>
            {u.backup && <p>{reverting
              ? t('updates.revertBackupNote', 'Database backup from before the revert: {{file}}', { file: `data/backups/${u.backup}` })
              : t('updates.backupNote', 'Database backup from before the update: {{file}}', { file: `data/backups/${u.backup}` })}</p>}
            {pageOutdated && (
              <button type="button" className="st-btn primary" onClick={() => window.location.reload()}>{t('updates.reload', 'Reload the page')}</button>
            )}
          </div>
        )}
        {!busy && !restarting && showFail && (
          <div className="up-error">
            <b>{u.status === 'rolled_back'
              ? (reverting ? t('updates.revertRolledBack', 'The revert failed and v{{v}} was started again.', { v: u.previous }) : t('updates.rolledBack', 'The update failed and the previous version was restored.'))
              : (reverting ? t('updates.revertFailed', 'The revert failed.') : t('updates.failed', 'The update failed.'))}</b>
            <p>{u.error}</p>
          </div>
        )}

        {/* available */}
        {u.available && !busy && !starting && !restarting && (
          <div className="up-available">
            <div>
              <div className="up-label">{isBeta(u.latest) ? t('updates.betaAvailable', 'Beta update available') : t('updates.available', 'Update available')}</div>
              <div className="up-ver">v{u.current} → <b>v{u.latest}</b>{isBeta(u.latest) && <span className="up-beta">{t('updates.betaTag', 'beta')}</span>}</div>
            </div>
            <button type="button" className="st-btn primary up-go" onClick={() => setConfirm(true)} disabled={live.length > 0}
              title={live.length ? t('updates.liveBlocked', 'Finish or wait for live matches to end') : undefined}>
              <SvIcon name="download" size={14} />{t('updates.updateNow', 'Update to v{{v}}', { v: u.latest })}
            </button>
          </div>
        )}
        {u.available && live.length > 0 && !busy && (
          <div className="up-warn">
            {t('updates.liveBlocked', 'Finish or wait for live matches to end')}:{' '}
            {live.map((m, i) => <React.Fragment key={m.id}>{i ? ', ' : ''}<Link to={`/admin/matches/${m.id}`}>#{m.id} {m.team1_name} vs {m.team2_name}</Link></React.Fragment>)}
          </div>
        )}
        {!u.available && !busy && !u.checkError && u.latest && !showDone && (
          <div className="up-muted">{t('updates.upToDate', 'You are on the latest version.')}</div>
        )}
        {actionError && <div className="up-error">{actionError}</div>}

        {/* beta channel: switched on/off on the VPS only (never from the panel). The command first
            copies setup.sh out of the running image: an install updated by hand can have an older
            setup.sh that doesn't know "beta" (it would run the whole setup again instead). */}
        {(() => {
          const dir = u.installDir || '/opt/flux';
          const on = u.channel === 'beta';
          const cmd = `cd ${dir} && sudo docker cp flux-app:/app/install/setup.sh setup.sh && sudo bash setup.sh beta ${on ? 'off' : 'on'}`;
          const step = (n, body) => (
            <div className="up-step">
              <span className="up-step-n">{n}</span>
              <div>{body}</div>
            </div>
          );
          return (
            <div className="up-revert column">
              <div>
                <div className="up-label">{on ? t('updates.betaOffTitle', 'Turn off beta versions') : t('updates.betaOnTitle', 'Get beta versions (test servers)')}</div>
                {!on && <p>{t('updates.betaWarn', 'Betas are test builds from the development branch, made on every change. Use them on test servers, not on a panel running real events.')}</p>}
              </div>
              {step(1, <>
                <p className="up-step-lead">{t('updates.betaStepRun', 'On the VPS (SSH, as root), run:')}</p>
                <div className="up-code-row">
                  <code className="up-code">{cmd}</code>
                  <CopyButton text={cmd} />
                </div>
              </>)}
              {on ? (
                step(2, <p>{t('updates.betaOffStepStay', 'Only release versions are offered from now on. The beta you have stays until a newer release comes out - or use "Revert to an older version" below to go back to a release now.')}</p>)
              ) : (<>
                {step(2, <p>{t('updates.betaStepCheck', 'Press Check now (top of this page). The "Beta channel" badge appears next to the version.')}</p>)}
                {step(3, <p>{t('updates.betaStepUpdate', 'New betas show up here as "Beta update available" - press Update. The database is backed up first, and "Revert to an older version" takes you back to a release at any time.')}</p>)}
              </>)}
            </div>
          );
        })()}

        {/* revert to an older release */}
        {older.length > 0 && !busy && !starting && !restarting && (
          <div className="up-revert">
            <div>
              <div className="up-label">{t('updates.revertTitle', 'Revert to an older version')}</div>
              <p>{t('updates.revertText', 'Installs an older release with the same safe steps as an update. Your data is kept.')}</p>
            </div>
            <div className="up-revert-go">
              <select className="up-select" value={pick} onChange={(e) => setRevertTo(e.target.value)} aria-label={t('updates.revertPick', 'Version')}>
                {older.map(v => <option key={v} value={v}>v{v}{isBeta(v) ? ` (${t('updates.betaTag', 'beta')})` : ''}</option>)}
              </select>
              <button type="button" className="st-btn" onClick={() => setConfirmRevert(true)} disabled={!pick || live.length > 0}
                title={live.length ? t('updates.liveBlocked', 'Finish or wait for live matches to end') : undefined}>
                <SvIcon name="refresh" size={13} />{t('updates.revertButton', 'Revert')}
              </button>
            </div>
          </div>
        )}

        {u.log && u.log.length > 0 && (
          <details className="up-log" open={busy || restarting}>
            <summary>{t('updates.log', 'Update log')}</summary>
            <pre>{u.log.map(l => l.replace(/^\S+\s/, '')).join('\n')}</pre>
          </details>
        )}
      </>)}

      <ConfirmModal
        show={confirm}
        message={t('updates.confirm', 'Update the panel to v{{v}}? The database is backed up first, the panel restarts for about 10 seconds, and it switches back by itself if the new version does not start.', { v: u?.latest })}
        confirmText={t('updates.updateNow', 'Update to v{{v}}', { v: u?.latest })}
        onConfirm={() => start(u.latest)}
        onClose={() => setConfirm(false)}
      />
      <ConfirmModal
        show={confirmRevert}
        message={t('updates.revertConfirm', 'Revert the panel from v{{from}} to v{{v}}? Features added after v{{v}} will not be available. The database is backed up first and kept as it is; the panel restarts for about 10 seconds, and it switches back by itself if v{{v}} does not start. You can update again at any time.', { from: running, v: pick })}
        confirmText={t('updates.revertTo', 'Revert to v{{v}}', { v: pick })}
        onConfirm={() => start(pick, true)}
        onClose={() => setConfirmRevert(false)}
      />
    </div>
  );
}
